import React, {memo, useCallback, useEffect, useState, useMemo} from "react";
import {useRPC} from "../../../../hooks/rpc";
import {
    CONTROL_TYPE_ID_SIREN,
    ControlModel,
    InputModel,
    SensorTypeModel,
    CameraModel,
} from "../../../../types/control";
import "./index.scss";
import {useDispatch} from "react-redux";
import {useMapSelector} from "../../../../hooks/map-selector";
import {
    entityUpdatedAction,
    inputSendErrorAction,
} from "../../../../store/control/actions";
import {Column} from "../../../../components/Column";
import {onClickHandler} from "../../../../utils/onclick";
import {PasswordContainer} from "../../../../components/PasswordContainer";
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer";
import {useSetSelector} from "../../../../hooks/set-selector";
import {useFilterSelector} from "../../../../hooks/filter-selector";
import {useLocalization} from "../../../../hooks/localization";
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent";
import {SettingSelectComponent} from "../../../../components/Settings/SettingSelectComponent";
import {SelectItem} from "../../../../components/Select";
import {useUpdate} from "../../../../hooks/update-effect";

const getImage = (
    sensor: InputModel,
    set: { [key: string]: SensorTypeModel }
) => {
    const type = set[sensor.sensor_settings.sensor_type_id];
    return type ? (sensor.is_active ? type.image_set.active : type.image_set.passive) : null
};

type SensorEditProps = {
    sensorOrSiren: InputModel | ControlModel;
    isInput: boolean;
    icon: string;
};

export const SensorEditComponent = memo(function SensorEditComponent({
       sensorOrSiren,
       icon,
       isInput,
    }: SensorEditProps) {
    const {t} = useLocalization();
    const [rpc] = useRPC();
    const dispatch = useDispatch();

    useEffect(() => {
        if (sensorOrSiren.hasError) {
            const timeout = setTimeout(
                () => dispatch(inputSendErrorAction(sensorOrSiren.id, false)),
                3000
            );
            return () => clearTimeout(timeout);
        }
    }, [dispatch, sensorOrSiren]);

    const camera_null_option = useMemo<SelectItem<any>[]>(
        () => [
            {
                value: 0,
                label: t("disable"),
            },
        ],
        [t]
    );

    const camera_list = useMapSelector(
        state => state.control.cameraList,
        camera => ({value: camera, label: camera.name}),
        []
    );

    const [sensorReverse, setSensorReverse] = useState(false);

    const [selectedCamera, setSelectedCamera] = useState<SelectItem<CameraModel>>(
        camera_list.find(el => el.value.id === sensorOrSiren.camera_id)
    );

    const setSensorReverseCheckbox = useCallback(() => {
        setSensorReverse(prev => !prev);
        rpc.updateNotificationStatus(sensorOrSiren.id, sensorReverse);
    }, [rpc, sensorOrSiren.id, sensorReverse]);

    useUpdate(() => {
        rpc.updateSensorCameraMatch(sensorOrSiren.id, selectedCamera.value.id);
    }, [rpc, sensorOrSiren.id, selectedCamera])

    /*******************************************************************************/
    const changeStatus = useCallback(
        (sensor: InputModel | ControlModel, is_active: boolean) => {
            if (is_active === sensor.is_active) {
                return;
            }
            dispatch(
                entityUpdatedAction(
                    {...sensor, isUpdating: true},
                    isInput ? "input" : "control"
                )
            );

            rpc.updateSensorStatus(sensor.id, is_active, !isInput);
        },
        [dispatch, rpc, isInput]
    );

    useEffect(() => {
        if (!camera_list.indexOf(camera_null_option[0])) return;
        camera_list.unshift(camera_null_option[0]);
    }, [camera_list, camera_null_option]);

    return (
        <Column min={12} max={12}>
            <div className="device">
                <div className="device-name">{sensorOrSiren.name}</div>
                <img src={icon} alt=""/>
                <div className="device-body">
                    <div
                        className={
                            "device-button " + (sensorOrSiren.is_active ? "selected" : "")
                        }
                        {...onClickHandler(() => changeStatus(sensorOrSiren, true))}
                    >
                        I
                    </div>
                    <div
                        className={
                            "device-button " + (sensorOrSiren.is_active ? "" : "selected")
                        }
                        {...onClickHandler(() => changeStatus(sensorOrSiren, false))}
                    >
                        0
                    </div>
                </div>
                <div className="sensor-checkboxes">
                    <span className="sensor-notification-symbol">*</span>
                    <div className="service-edit">
                        <div className="content-divider">
                            <div
                                className="content-wrapper"
                                {...onClickHandler(() => {
                                    setSensorReverse(!sensorReverse);
                                })}
                            >
                                <SettingCheckBoxComponent
                                    label={""}
                                    set={setSensorReverseCheckbox}
                                    value={sensorOrSiren.is_notification}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="sensor-camera-selectbox-container">
                    <SettingSelectComponent
                        label={t("")}
                        options={camera_list}
                        value={selectedCamera}
                        onChange={setSelectedCamera}
                    />
                </div>

                {renderIf(sensorOrSiren.isUpdating)(() => (
                    <div className="open-close-button-loading"/>
                ))}
                {renderIf(sensorOrSiren.hasError)(() => (
                    <div className="open-close-button-loading darker">
                        <span>{t("DEVICE_NOT_REACHABLE")}</span>
                    </div>
                ))}
            </div>
        </Column>
    );
});

export const SensorSettingsScreen = memo(function SensorSettingsScreen() {
    const {t} = useLocalization();
    const inputTypeSet = useSetSelector(state => state.control.inputTypeList);
    const sensorList = useFilterSelector(
        state => state.control.inputList,
        i => inputTypeSet[i.type_id]?.primitive_name === "sensor",
        [inputTypeSet]
    );

    const sensorTypeSet = useSetSelector(state => state.control.sensorTypeList);
    const sirenList = useFilterSelector(
        state => state.control.controlList,
        c => c.type_id === CONTROL_TYPE_ID_SIREN,
        []
    );

    const controlTypeSet = useSetSelector(
        state => state.control.controlTypeList
    );

    return (
        <PasswordContainer requiredRole="admin" isFullscreen={true}>
            <SettingsPageContainer buttons={[]} backUrl={"/settings"}>
                <div className="sensor-page-info-text">
                    <span>*</span>
                    {t("input_notification")}
                </div>
                <div className="page-wrapper">
                    <div className="sensor-settings">
                        {sensorList.map((sensor) => (
                            <SensorEditComponent
                                key={`sensor_edit_${sensor.id}`}
                                icon={getImage(sensor, sensorTypeSet)}
                                sensorOrSiren={sensor}
                                isInput={true}
                            />
                        ))}
                        {sirenList.map((siren) => (
                            <SensorEditComponent
                                key={`siren_edit_${siren.id}`}
                                icon={controlTypeSet[siren.type_id]?.image_set?.default || null}
                                sensorOrSiren={siren}
                                isInput={false}
                            />
                        ))}
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
